import {
  BufferAttribute,
  BufferGeometry,
  Camera,
  Mesh,
  MeshBasicMaterial,
  Texture,
  Vector3,
  WebGLRenderer,
} from 'three';

import { FlatPlaneWorldMapping } from '../ortho-tile-asset/flat-plane-world-mapping';
import {
  TileCoord,
  TileKey,
  tileKey,
  WorldMapping,
  WorldQuad,
} from '../ortho-tile-asset/ortho-tile-typings';
import { Point2 } from './cog-projection';
import { CogSource } from './cog-source';
import { COG_MASK_TEXTURE } from './cog-tile-loader';

export type CogMaterialOptions = Omit<
  ConstructorParameters<typeof FlatPlaneWorldMapping>[0],
  'tileBoundsToWorldQuad'
>;

/**
 * Places COG tiles on the flat ground plane.
 *
 * Differs from the XYZ mapping in that tiles are positioned from the COG's own CRS and
 * edge tiles are clipped to the image extent, with UVs scaled to the valid part of the
 * padded tile texture.
 */
export class CogWorldMapping implements WorldMapping {
  private readonly flat: FlatPlaneWorldMapping;
  /** Projected quads are hit several times per tile per frame by the LOD selector. */
  private readonly quads = new Map<TileKey, WorldQuad>();

  constructor(
    private readonly params: {
      source: CogSource;
      cogToProject: (point: Point2) => Point2;
      /** Project coords at scene (0,0). */
      sceneOriginProject: { x: number; y: number };
      /**
       * A materialGenerator replaces the default material, and must apply the mask itself
       * (texture.userData[COG_MASK_TEXTURE]) for tiles to be transparent outside the image.
       */
      material: CogMaterialOptions;
    },
  ) {
    this.flat = new FlatPlaneWorldMapping({
      ...params.material,
      tileBoundsToWorldQuad: (coord) => this.tileBoundsToWorldQuad(coord),
    });
  }

  tileToWorldGeometry(input: { coord: TileCoord }): BufferGeometry {
    const q = this.tileBoundsToWorldQuad(input.coord);
    const { source } = this.params;
    const tile = source.getTile(input.coord);
    const uMax = tile ? tile.validWidth / source.tileWidth : 1;
    // Bitmaps are flipped on decode, so the top image row is at v = 1
    const vMin = tile ? 1 - tile.validHeight / source.tileHeight : 0;

    // Same layout as the XYZ quad: (a,b,d) and (b,c,d) with a=SW, b=SE, c=NE, d=NW
    const geom = new BufferGeometry();
    const corners = [q.a, q.b, q.d, q.b, q.c, q.d];
    geom.setAttribute(
      'position',
      new BufferAttribute(new Float32Array(corners.flatMap((p) => [p.x, p.y, p.z])), 3),
    );
    geom.setAttribute(
      'uv',
      new BufferAttribute(
        new Float32Array([0, vMin, uMax, vMin, 0, 1, uMax, vMin, uMax, 1, 0, 1]),
        2,
      ),
    );
    return geom;
  }

  getTileWorldCorners(coord: TileCoord): Vector3[] {
    return this.flat.getTileWorldCorners(coord);
  }

  estimateScreenPx(cornersWorld: Vector3[], camera: Camera, renderer: WebGLRenderer): number {
    return this.flat.estimateScreenPx(cornersWorld, camera, renderer);
  }

  createTileMesh(geometry: BufferGeometry): Mesh {
    return this.flat.createTileMesh(geometry);
  }

  applyTexture(mesh: Mesh, texture: Texture): void {
    this.flat.applyTexture(mesh, texture);
    const mat = mesh.material;
    if (mat instanceof MeshBasicMaterial) {
      mat.alphaMap = (texture.userData[COG_MASK_TEXTURE] as Texture | undefined) ?? null;
      mat.needsUpdate = true;
    }
  }

  disposeTileMesh(mesh: Mesh): void {
    this.flat.disposeTileMesh(mesh);
  }

  private tileBoundsToWorldQuad(coord: TileCoord): WorldQuad {
    const key = tileKey(coord);
    const cached = this.quads.get(key);
    if (cached) {
      return cached;
    }

    const { source, cogToProject, sceneOriginProject: origin } = this.params;
    const b = source.getTileBounds(coord);
    const toScene = (x: number, y: number) => {
      const [px, py] = cogToProject([x, y]);
      return new Vector3(px - origin.x, py - origin.y, 0);
    };

    const quad: WorldQuad = {
      a: toScene(b.minX, b.minY),
      b: toScene(b.maxX, b.minY),
      c: toScene(b.maxX, b.maxY),
      d: toScene(b.minX, b.maxY),
    };
    this.quads.set(key, quad);
    return quad;
  }
}
